/* eslint-disable react/jsx-key */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import circleCheck from "@sonic/Assets/Icons/circleCheck";
import circleClose from "@sonic/Assets/Icons/circleClose";
import { ProductLineDisplay } from "@sonic/CustomComponents/ProductLineDisplay";
import { ProductDetailsPageContext } from "@sonic/Overrides/Pages/ProductDetailsPage";
import updateProductDetailNavigationState from "@sonic/Store/Components/ProductDetailNavigation/Handlers/UpdateProductDetailNavigationState";
import { customerState } from "@sonic/Store/GemecoState";
import { mappedAnchorLink } from "@sonic/Widgets/Common/AnchorNavigation";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const iconMap = {
    false: circleClose,
    true: circleCheck,
    none: undefined,
} as const;

const enum iconTypes {
    icon = "icon",
}
type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
interface OwnProps extends WidgetProps {
    iconTypes: {
        [iconTypes.icon]: keyof typeof iconMap;
    };
}

const mapStateToProps = (state: customerState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    anchorNavigation: state.productDetailNavigation,
});

const mapDispatchToProps = {
    updateProductDetailNavigationState,
};

export interface ProductSpecsStyle {
    titleStyling?: TypographyPresentationProps;
    container?: InjectableCss;
    specContainer?: InjectableCss;
    featuresContainer?: InjectableCss;
    features?: InjectableCss;
    dimensions?: InjectableCss;
    featureLayout?: InjectableCss;
    margin?: InjectableCss;
    leftGridItemStyle: GridItemProps;
    rightGridItemStyle: GridItemProps;
    attributeTypeContainer?: InjectableCss;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    imageContainer?: InjectableCss;
    imageStyles?: InjectableCss;
    metaDescription?: InjectableCss;
    buttonToggle?: HiddenProps;
    hideTitle?: HiddenProps;
}

export const productSpecsStyle: ProductSpecsStyle = {
    titleStyling: {
        size: 25,
        css: css`
            display: block;
            width: 100%;
            margin-bottom: 30px;
            font-weight: normal;

            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        text-align: left;
                        margin-bottom: 0;
                    `,
                    css`
                        text-align: left;
                        margin-bottom: 0;
                    `,
                ]);
            }}
        `,
    },
    container: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    attributeTypeWrapper: {
        css: css`
            margin-bottom: 18px;
            width: 100%;
            display: flex;
            justify-content: space-between;
            gap: 20px;

            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 10px;
                    `,
                    css`
                        margin-bottom: 10px;
                        padding: 0 16px;
                    `,
                ]);
            }}
        `,
    },
    attributeTypeContainer: {
        css: css`
            width: 100%;
            align-self: flex-start;
        `,
    },
    attributeTypeLabelText: {
        color: "#808080",
        weight: "normal",
    },
    attributeTypeValuesText: {
        css: css`
            max-width: 70%;
            text-align: right;
        `,
    },
    specContainer: {
        css: css`
            width: 50%;
        `,
    },
    featuresContainer: {
        css: css`
            position: relative;
            border-bottom: 1px solid #00000026;
            padding-bottom: 50px;
            scroll-margin-top: 320px;
        `,
    },
    featureLayout: {
        css: css`
            display: flex;
            color: grey;
            justify-content: space-between;
            max-width: 400px;
            margin-bottom: 10px;
        `,
    },
    margin: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    imageContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 80px;

            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        gap: 30px;
                    `,
                    css`
                        gap: 30px;
                    `,
                    css`
                        gap: 80px;
                    `,
                    css`
                        gap: 80px;
                    `,
                    css`
                        gap: 80px;
                    `,
                ]);
            }}
        `,
    },
    imageStyles: {
        css: css`
            width: 100%;
            height: 270px;
            text-align: center;

            img {
                height: 100%;
                width: 100%;
                object-fit: contain;
            }
        `,
    },
    leftGridItemStyle: {
        width: [12, 12, 6, 6, 6],
    },
    rightGridItemStyle: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    metaDescription: {
        css: css`
            padding-bottom: 30px;
        `,
    },
    buttonToggle: {
        above: "sm",
        css: css`
            display: flex;
            flex: 1;
        `,
    },
    hideTitle: {
        below: "md",
    },
};

const ProductFeatureAndSpecs: React.FC<Props> = props => {
    const { id, anchorNavigation, product, updateProductDetailNavigationState } = props;
    const [showState, setState] = React.useState(true);
    const [specificationsActive, setSpecificationsActive] = React.useState(false);
    const [features, setFeatures] = React.useState<SpecificationModel[]>([]);
    const [specs, setSpecs] = React.useState<SpecificationModel[]>([]);
    const [anchorState, setAnchorState] = React.useState<mappedAnchorLink>();
    const { attributeTypes, specifications } = product;
    const styles = productSpecsStyle;
    const specificationImages = product.images?.filter(image => image.imageType !== "Static");
    const hasFeatures = features.length > 0;
    const filterFeatures = (spec: SpecificationModel) => {
        const fitlerValues = ["true", "false"];
        if (fitlerValues.includes(spec.value)) {
            return true;
        }
        return false;
    };

    const filterSpecs = (spec: SpecificationModel) => {
        const fitlerValues = ["true", "false"];
        if (fitlerValues.includes(spec.value)) {
            return false;
        }
        return true;
    };

    // Sonic anchornavstate
    React.useEffect(() => {
        const translated = translate("Features & Specifications");
        const link: mappedAnchorLink = {
            active: true,
            contentName: translated,
            contentID: translated.split(" & ").join("-"),
            sortOrder: 1,
        };
        setAnchorState(link);
    }, []);

    React.useEffect(() => {
        const hasAnchorAlready = anchorNavigation.navigationLinks.find(
            (link: mappedAnchorLink) => link.contentID === anchorState?.contentID,
        );
        if (!hasAnchorAlready && anchorState) {
            updateProductDetailNavigationState({ navigationLinks: [anchorState] });
        }
    }, [anchorState, anchorState?.active]);

    React.useEffect(() => {
        if (!specifications) {
            return;
        }
        const filteredFeatures = specifications.filter(spec => filterFeatures(spec));
        setFeatures(filteredFeatures);
        const filteredSpecs = specifications.filter(spec => filterSpecs(spec));
        setSpecs(filteredSpecs);
    }, [specifications]);

    React.useEffect(() => {
        setSpecificationsActive(!showState);
    }, [showState]);

    const toggleLeft = {
        css: css`
            color: ${showState ? "#000" : "#666666"};
            background-color: ${showState ? "#fff" : "#C4C4C466"};
            border: none;
            border-radius: 0;
            padding: 16px 24px 16px 16px;
        `,
    };

    const toggleRight = {
        css: css`
            color: ${showState ? "#666666" : "#000"};
            background-color: ${showState ? "#C4C4C466" : "#fff"};
            border: none;
            border-radius: 0;
            padding: 16px 16px 16px 24px;
        `,
    };

    // const hoverToggle = {
    //     backgroundColor: 'RGBA(196,196,196, 0.17)',
    //     color: '#000',
    // }

    styles.rightGridItemStyle.css = css`
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        padding: 30px 0;
        ${({ theme }: { theme: BaseTheme }) => {
            return breakpointMediaQueries(theme, [
                css`
                    padding: 30px 16px;
                    display: ${showState ? "none" : "flex"};
                `,
                css`
                    padding: 30px 16px;
                    display: ${showState ? "none" : "flex"};
                `,
                css`
                    padding: 0 44px;
                `,
                css`
                    padding: 0 44px;
                `,
                css`
                    padding: 0 44px;
                `,
            ]);
        }}
    `;

    styles.leftGridItemStyle.css = css`
        flex-wrap: wrap;
        border-right: 1px solid #00000026;
        padding: 30px 15px;
        ${({ theme }: { theme: BaseTheme }) => {
            return breakpointMediaQueries(theme, [
                css`
                    padding: 30px 16px;
                    border-right: none;
                    display: ${showState ? "flex" : "none"};
                `,
                css`
                    padding: 30px 16px;
                    border-right: none;
                    display: ${showState ? "flex" : "none"};
                `,
                css`
                    padding: 0 44px;
                `,
                css`
                    padding: 0 44px;
                `,
                css`
                    padding: 0 44px;
                `,
            ]);
        }}
    `;

    return (
        <>
            <Hidden {...styles.buttonToggle}>
                <Button {...toggleLeft} onClick={e => setState(true)}>
                    <Typography {...styles.titleStyling} variant="headerSecondary" size={25}>
                        {translate("Specifications & Dimensions")}
                    </Typography>
                </Button>
                <Button {...toggleRight} onClick={e => setState(false)}>
                    <Typography {...styles.titleStyling} variant="headerSecondary" size={25}>
                        {translate("Product Features")}
                    </Typography>
                </Button>
            </Hidden>
            <GridContainer id={anchorState?.contentID} {...styles.featuresContainer}>
                <GridItem align="top" {...styles.leftGridItemStyle}>
                    <Hidden {...styles.hideTitle}>
                        <Typography {...styles.titleStyling} variant="headerSecondary" size={25}>
                            {translate("Product Features")}
                        </Typography>
                    </Hidden>
                    {product.content && (
                        <StyledWrapper {...styles.metaDescription}>
                            <Typography size={16}>{parse(product.content.htmlContent, parserOptions)}</Typography>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.imageContainer}>
                        {specificationImages &&
                            specificationImages.map(image => {
                                return (
                                    <StyledWrapper key={image.id} {...styles.imageStyles}>
                                        <Img
                                            width="100%"
                                            height="100%"
                                            src={image.largeImagePath}
                                            altText={image.imageAltText}
                                        ></Img>
                                    </StyledWrapper>
                                );
                            })}
                    </StyledWrapper>
                </GridItem>
                <GridItem align="top" {...styles.rightGridItemStyle}>
                    {hasFeatures && (
                        <Hidden {...styles.hideTitle}>
                            <Typography {...styles.titleStyling} size={25} variant="headerSecondary">
                                {translate("Features")}
                            </Typography>
                        </Hidden>
                    )}
                    {hasFeatures &&
                        features.map(spec => {
                            const hasSpec = spec.value as keyof typeof iconMap;
                            const icon = iconMap[hasSpec || "none"];
                            return (
                                <StyledWrapper key={spec.id} {...styles.attributeTypeWrapper}>
                                    <Typography {...styles.attributeTypeLabelText} size={18} variant="headerSecondary">
                                        {spec.nameDisplay || spec.name}
                                    </Typography>
                                    <Icon src={icon}></Icon>
                                </StyledWrapper>
                            );
                        })}
                    {specifications && (
                        <StyledWrapper {...styles.attributeTypeContainer}>
                            {attributeTypes && (
                                <>
                                    <Typography {...styles.titleStyling} variant="headerSecondary" size={25}>
                                        {translate("Specifications")}
                                    </Typography>
                                    {attributeTypes.map(attributeType => {
                                        if (attributeType.label.toLowerCase() === "type of filling trolley") {
                                            return (
                                                <StyledWrapper key={attributeType.id} {...styles.attributeTypeWrapper}>
                                                    <Typography
                                                        {...styles.attributeTypeLabelText}
                                                        size={18}
                                                        variant="headerSecondary"
                                                    >
                                                        {translate("Type of filling trolley")}:&nbsp;
                                                    </Typography>
                                                    <ProductLineDisplay product={product} variant="small" />
                                                </StyledWrapper>
                                            );
                                        }
                                        return (
                                            <StyledWrapper
                                                {...styles.attributeTypeWrapper}
                                                key={attributeType.id.toString()}
                                            >
                                                <Typography
                                                    {...styles.attributeTypeLabelText}
                                                    size={18}
                                                    variant="headerSecondary"
                                                >
                                                    {attributeType.label || attributeType.name}:&nbsp;
                                                </Typography>
                                                <Typography {...styles.attributeTypeValuesText}>
                                                    {attributeType
                                                        .attributeValues!.map(
                                                            attributeValue => attributeValue.valueDisplay,
                                                        )
                                                        .join(", ")}
                                                </Typography>
                                            </StyledWrapper>
                                        );
                                    })}
                                </>
                            )}
                            {specs.map(spec => (
                                <StyledWrapper key={spec.id} {...styles.attributeTypeWrapper}>
                                    <Typography {...styles.attributeTypeLabelText} size={18} variant="headerSecondary">
                                        {spec.nameDisplay || spec.name}
                                    </Typography>
                                    <Typography {...styles.attributeTypeValuesText}>{spec.value}</Typography>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    )}
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductFeatureAndSpecs)),
    definition: {
        group: "Product Details",
        icon: "Product detail",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
